/* RESPONSIBLE TEAM: team-inbox */

import { Resource } from 'ember-resources/core';
import { type Named } from 'ember-resources/core/types';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { registerDestructor } from '@ember/destroyable';
import { dropTask } from 'ember-concurrency-decorators';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type CopilotApi from 'embercom/services/copilot-api';
import type InboxState from 'embercom/services/inbox-state';
import type LogService from 'embercom/services/log-service';
import type Conversation from 'embercom/objects/inbox/conversation';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { TrackedArray } from 'tracked-built-ins';
import { type BlockList } from '@intercom/interblocks.ts';
import { type PublicAPI as ComposerAPI } from 'embercom/components/inbox2/conversation-reply-composer';

interface CopilotSuggestionsResponse {
  conversationId: number;
  answerBotTransactionId: string;
  suggestions: CopilotSuggestion[];
}

interface CopilotQuestionSuggestionsArgs {
  conversationId?: Conversation['id'];
  userCommentId?: RenderablePart['id'];
  hasSuggestionsPinned?: boolean;
  isConversationLoading?: boolean;
  hasIngestedContent?: boolean;
}

export type CopilotSuggestion =
  | {
      type: 'question';
      question: string;
      emoji: string;
      generatedForInboxConversationId: number;
      answerBotTransactionId: string;
    }
  | {
      type: 'macro';
      macro: {
        id: number;
        name: string;
        blocks: BlockList;
      };
      generatedForInboxConversationId: number;
      answerBotTransactionId: string;
    };

type CopilotSuggestionWithType<T extends CopilotSuggestion['type']> = Extract<
  CopilotSuggestion,
  { type: T }
>;

export default class CopilotQuestionSuggestions extends Resource<
  Named<CopilotQuestionSuggestionsArgs>
> {
  @service declare copilotApi: CopilotApi;
  @service declare inboxState: InboxState;
  @service declare logService: LogService;

  private lastConversationId?: number;
  private lastUserCommentId?: number;
  private previousIsConversationLoading?: boolean;
  private previousHasSuggestionsPinned?: boolean;

  private suggestionsUsedInCurrentConversation = new TrackedArray<string>();
  private macrosUsedInCurrentConversation = new TrackedArray<number>();

  @tracked private _suggestionsResponse: CopilotSuggestionsResponse | undefined;

  @dropTask
  *fetchSuggestionsTask() {
    // Fake task that does nothing.
    yield;
  }

  constructor(owner: unknown) {
    super(owner);
    registerDestructor(this, this.teardown);
  }

  modify(_: never, args: CopilotQuestionSuggestionsArgs) {
    let shouldFetch = false;
    let shouldTearDown = false;

    // Determine if we should fetch based on conversation or user comment changes
    if (
      this.lastConversationId !== args.conversationId ||
      this.lastUserCommentId !== args.userCommentId
    ) {
      shouldTearDown = true;
      shouldFetch = true;
      if (this.lastConversationId !== args.conversationId) {
        this.suggestionsUsedInCurrentConversation = new TrackedArray<string>();
      }
    }

    // Determine if we should fetch based on conversation loading state
    if (this.previousIsConversationLoading !== args.isConversationLoading) {
      shouldTearDown = true;
      shouldFetch = !args.isConversationLoading;
    }

    // Determine if we should fetch based on suggestions pinned state
    if (this.previousHasSuggestionsPinned !== args.hasSuggestionsPinned) {
      shouldTearDown = true;
      shouldFetch = !!args.hasSuggestionsPinned;
    }

    // We should not fetch if there is no ingested content
    if (!args.hasIngestedContent) {
      shouldTearDown = true;
      shouldFetch = false;
    }

    // Tear down if necessary
    // This is done before fetching to ensure that the fetch task is cancelled
    if (shouldTearDown || shouldFetch) {
      this.teardown();
    }

    // Update last known states
    this.lastConversationId = args.conversationId;
    this.lastUserCommentId = args.userCommentId;
    this.previousIsConversationLoading = args.isConversationLoading;
    this.previousHasSuggestionsPinned = args.hasSuggestionsPinned;

    // Perform fetch if conditions are met
    if (
      shouldFetch &&
      this.lastConversationId &&
      !this.previousIsConversationLoading &&
      this.previousHasSuggestionsPinned
    ) {
      taskFor(this.fetchSuggestionsTask).perform();
    }
  }

  // Public API

  get visibleSuggestions(): CopilotSuggestion[] | undefined {
    if (this.shouldShowSuggestions) {
      return this.suggestions;
    }
    return;
  }

  get visibleQuestionSuggestions(): CopilotSuggestionWithType<'question'>[] | undefined {
    // If a macro is suggested, we don't want to show question suggestions
    if (this.visibleMacroSuggestion) {
      return;
    }

    return this.visibleSuggestions?.filter(
      (suggestion): suggestion is CopilotSuggestionWithType<'question'> =>
        suggestion.type === 'question',
    );
  }

  get visibleMacroSuggestion() {
    return this.visibleSuggestions?.find((suggestion) => suggestion.type === 'macro');
  }

  @action handleSuggestionClick(
    suggestion: CopilotSuggestion,
    { insertBlocks }: { insertBlocks: ComposerAPI['insertBlocks'] },
  ) {
    if (!this.lastConversationId) {
      return;
    }
    this.assertGuardrails('sendCopilotSuggestion', suggestion.generatedForInboxConversationId);

    if (suggestion.type === 'macro') {
      this.markMacroAsUsedForActiveConversation(suggestion.macro.id);
      insertBlocks(suggestion.macro.blocks, suggestion.generatedForInboxConversationId);
    } else {
      this.markSuggestionAsUsedForActiveConversation(suggestion.question);
      this.inboxState.conversationsThatUsedSuggestions.add(this.lastConversationId);
      this.copilotApi.triggerAndSearchCopilotQuery(suggestion.question);
    }
  }

  @action handleSuggestionRejected(suggestion: CopilotSuggestion) {
    if (suggestion.type === 'macro') {
      this.markMacroAsUsedForActiveConversation(suggestion.macro.id);
    } else {
      this.markSuggestionAsUsedForActiveConversation(suggestion.question);
    }
  }

  // Private API

  private get shouldShowSuggestions(): boolean {
    // we only want to show suggestions when a teammate hasn't interacted with them in this session
    return !!(
      this.lastConversationId &&
      !this.inboxState.conversationsThatUsedSuggestions.has(this.lastConversationId)
    );
  }

  private get suggestions(): CopilotSuggestion[] | undefined {
    if (this.isLoading || !this._suggestionsResponse) {
      return;
    }
    let { conversationId, answerBotTransactionId, suggestions } = this._suggestionsResponse;

    this.assertGuardrails('showCopilotSuggestion', conversationId);

    return suggestions
      .filter((suggestion: CopilotSuggestion) =>
        suggestion.type === 'macro'
          ? !this.macrosUsedInCurrentConversation.includes(suggestion.macro.id)
          : !this.suggestionsUsedInCurrentConversation.includes(suggestion.question),
      )
      .map((suggestion: CopilotSuggestion) => ({
        ...suggestion,
        generatedForInboxConversationId: conversationId,
        answerBotTransactionId,
      }));
  }

  @action private teardown() {
    this._suggestionsResponse = undefined;
    taskFor(this.fetchSuggestionsTask).cancelAll();
  }
  private markSuggestionAsUsedForActiveConversation(suggestion: string) {
    this.suggestionsUsedInCurrentConversation.push(suggestion);
  }

  private markMacroAsUsedForActiveConversation(macroId: number) {
    this.macrosUsedInCurrentConversation.push(macroId);
  }

  private get isLoading() {
    return taskFor(this.fetchSuggestionsTask).isRunning;
  }

  private assertGuardrails(location: string, conversationId: Conversation['id']) {
    if (conversationId !== this.lastConversationId) {
      let error = new Error('Attempted to show copilot suggestion from a different conversation');
      let stack = error.stack?.split('\n').slice(1, 15).join('\n');
      this.logService.log({
        log_type: location,
        actual_conversation_id: this.lastConversationId,
        expected_conversation_id: conversationId,
        stack,
      });

      throw error;
    }
  }
}
